import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ScrollToTop from "@/components/ScrollToTop";

// Public pages
import Index from "./pages/Index";
import Services from "./pages/Services";
import Solutions from "./pages/Solutions";
import MediaActivations from "./pages/MediaActivations";
import Analytics from "./pages/Analytics";
import CaseStudies from "./pages/CaseStudies";
import About from "./pages/About";
import Contact from "./pages/Contact";
import EmailMarketing from "./pages/EmailMarketing";
import LifecycleMarketing from "./pages/LifecycleMarketing";
import LoyaltyPrograms from "./pages/LoyaltyPrograms";
import CRMStrategy from "./pages/CRMStrategy";
import SEOPublic from "./pages/SEO";
import WebDevelopment from "./pages/WebDevelopment";
import PaidMarketing from "./pages/PaidMarketing";
import PaidSearch from "./pages/PaidSearch";
import PaidSocial from "./pages/PaidSocial";
import Programmatic from "./pages/Programmatic";
import CRO from "./pages/CRO";
import SocialMedia from "./pages/SocialMedia";
import AffiliateMarketing from "./pages/AffiliateMarketing";
import DataManagement from "./pages/DataManagement";
import AIAutomation from "./pages/AIAutomation";
import GoogleCloudPlatform from "./pages/GoogleCloudPlatform";
import LandingPages from "./pages/LandingPages";
import UXStrategy from "./pages/UXStrategy";
import Creative from "./pages/Creative";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import CaseStudyPost from "./pages/CaseStudyPost";
import Guides from "./pages/Guides";
import FAQs from "./pages/FAQs";
import Career from "./pages/Career";
import Tools from "./pages/Tools";
import NotFound from "./pages/NotFound";

// Resources pages
import ResourcesHub from "./pages/resources/ResourcesHub";
import Insights from "./pages/resources/Insights";
import GuidesPage from "./pages/resources/GuidesPage";
import CaseStudiesPage from "./pages/resources/CaseStudiesPage";
import FrameworksPage from "./pages/resources/FrameworksPage";
import FAQsPage from "./pages/resources/FAQsPage";

// Resources deep pages
import WhyGrowthStrategiesFail from "./pages/resources/insights/WhyGrowthStrategiesFail";
import SEOPlaybookB2B from "./pages/resources/guides/SEOPlaybookB2B";
import GrowthSystemsFramework from "./pages/resources/frameworks/GrowthSystemsFramework";
import SaaSAcquisitionCase from "./pages/resources/case-studies/SaaSAcquisitionCase";

// Solutions sub-pages
import EcommerceGrowth from "./pages/solutions/EcommerceGrowth";
import SaaSAcceleration from "./pages/solutions/SaaSAcceleration";
import D2CScale from "./pages/solutions/D2CScale";
import EnterprisePerformance from "./pages/solutions/EnterprisePerformance";

// Media Activations sub-pages
import InfluencerMarketing from "./pages/media/InfluencerMarketing";
import PerformanceMediaBuying from "./pages/media/PerformanceMediaBuying";
import BrandActivations from "./pages/media/BrandActivations";

// Analytics sub-pages
import GA4Implementation from "./pages/analytics/GA4Implementation";
import AttributionModeling from "./pages/analytics/AttributionModeling";

// Tools pages
import ROICalculator from "./pages/tools/ROICalculator";
import ROASCalculator from "./pages/tools/ROASCalculator";
import UTMBuilder from "./pages/tools/UTMBuilder";
import CTRCalculator from "./pages/tools/CTRCalculator";
import CPACalculator from "./pages/tools/CPACalculator";
import ConversionCalculator from "./pages/tools/ConversionCalculator";
import CPCCalculator from "./pages/tools/CPCCalculator";
import CPMCalculator from "./pages/tools/CPMCalculator";
import ChurnCalculator from "./pages/tools/ChurnCalculator";
import CLVCalculator from "./pages/tools/CLVCalculator";
import NPSCalculator from "./pages/tools/NPSCalculator";
import KeywordDensityCalculator from "./pages/tools/KeywordDensityCalculator";
import ReadabilityChecker from "./pages/tools/ReadabilityChecker";
import LoremIpsumGenerator from "./pages/tools/LoremIpsumGenerator";
import ColorPicker from "./pages/tools/ColorPicker";

// Admin pages
import AdminLayout from "./layouts/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import Dashboard from "./pages/admin/Dashboard";
import PagesNew from "./pages/admin/PagesNew";
import RedirectsNew from "./pages/admin/RedirectsNew";
import SEOAdmin from "./pages/admin/SEO";
import ContentEditor from "./pages/admin/ContentEditor";
import PageContentManager from "./pages/admin/PageContentManager";
import ContentItemsManager from "./pages/admin/ContentItemsManager";
import AuditLogViewer from "./pages/admin/AuditLogViewer";
import SEOHygieneDashboard from "./pages/admin/SEOHygieneDashboard";
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/media-activations" element={<MediaActivations />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/email-marketing" element={<EmailMarketing />} />
            <Route path="/lifecycle-marketing" element={<LifecycleMarketing />} />
            <Route path="/loyalty-programs" element={<LoyaltyPrograms />} />
            <Route path="/crm-strategy" element={<CRMStrategy />} />
            <Route path="/seo" element={<SEOPublic />} />
            <Route path="/web-development" element={<WebDevelopment />} />
            <Route path="/paid-marketing" element={<PaidMarketing />} />
            <Route path="/paid-search" element={<PaidSearch />} />
            <Route path="/paid-social" element={<PaidSocial />} />
            <Route path="/programmatic" element={<Programmatic />} />
            <Route path="/cro" element={<CRO />} />
            <Route path="/social-media" element={<SocialMedia />} />
            <Route path="/affiliate-marketing" element={<AffiliateMarketing />} />
            <Route path="/data-management" element={<DataManagement />} />
            <Route path="/ai-automation" element={<AIAutomation />} />
            <Route path="/google-cloud-platform" element={<GoogleCloudPlatform />} />
            <Route path="/landing-pages" element={<LandingPages />} />
            <Route path="/ux-strategy" element={<UXStrategy />} />
            <Route path="/creative" element={<Creative />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/case-studies/:slug" element={<CaseStudyPost />} />
            <Route path="/career" element={<Career />} />
            <Route path="/tools" element={<Tools />} />
            
            {/* Solutions Sub-Routes */}
            <Route path="/solutions/ecommerce-growth" element={<EcommerceGrowth />} />
            <Route path="/solutions/saas-acceleration" element={<SaaSAcceleration />} />
            <Route path="/solutions/d2c-scale" element={<D2CScale />} />
            <Route path="/solutions/enterprise-performance" element={<EnterprisePerformance />} />
            
            {/* Media Activations Sub-Routes */}
            <Route path="/media/influencer-marketing" element={<InfluencerMarketing />} />
            <Route path="/media/performance-media-buying" element={<PerformanceMediaBuying />} />
            <Route path="/media/brand-activations" element={<BrandActivations />} />
            
            {/* Analytics Sub-Routes */}
            <Route path="/analytics/ga4-implementation" element={<GA4Implementation />} />
            <Route path="/analytics/attribution-modeling" element={<AttributionModeling />} />
            
            {/* Tools Routes */}
            <Route path="/tools/roi-calculator" element={<ROICalculator />} />
            <Route path="/tools/roas-calculator" element={<ROASCalculator />} />
            <Route path="/tools/utm-builder" element={<UTMBuilder />} />
            <Route path="/tools/ctr-calculator" element={<CTRCalculator />} />
            <Route path="/tools/cpa-calculator" element={<CPACalculator />} />
            <Route path="/tools/conversion-calculator" element={<ConversionCalculator />} />
            <Route path="/tools/cpc-calculator" element={<CPCCalculator />} />
            <Route path="/tools/cpm-calculator" element={<CPMCalculator />} />
            <Route path="/tools/churn-calculator" element={<ChurnCalculator />} />
            <Route path="/tools/clv-calculator" element={<CLVCalculator />} />
            <Route path="/tools/nps-calculator" element={<NPSCalculator />} />
            <Route path="/tools/keyword-density" element={<KeywordDensityCalculator />} />
            <Route path="/tools/readability-checker" element={<ReadabilityChecker />} />
            <Route path="/tools/lorem-ipsum" element={<LoremIpsumGenerator />} />
            <Route path="/tools/color-picker" element={<ColorPicker />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="content" element={<PageContentManager />} />
              <Route path="content/legacy" element={<ContentEditor />} />
              <Route path="content-items" element={<ContentItemsManager />} />
              <Route path="seo-hygiene" element={<SEOHygieneDashboard />} />
              <Route path="redirects" element={<Dashboard />} />
              <Route path="redirects/new" element={<RedirectsNew />} />
              <Route path="seo" element={<SEOAdmin />} />
              <Route path="audit" element={<AuditLogViewer />} />
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
